import {
  AspectRatio,
  Body,
  Box,
  Heading,
  Image,
  system,
} from '@hover/blueprint';
import React from 'react';
import { Trans } from 'react-i18next';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import dreamSpaceImage from 'assets/img/designLandingPage/dreamSpace.jpg';
import makeItReal from 'assets/img/designLandingPage/makeItReal.jpg';
import yourHomeImage from 'assets/img/designLandingPage/yourHome.jpg';

type Props = {
  onAddPhotoClick: () => void;
};

const DesktopLineBreak = () => (
  <system.br display={{ base: 'none', desktop: 'inline' }} />
);

const SectionCard = ({
  index,
  title,
  description,
  image,
  onAddPhotoClick,
}: {
  index: number;
  title: string | JSX.Element;
  description: string;
  image: string;
  onAddPhotoClick: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGNER_LANDING_PAGE);
  const { trackingData } = useHomeownerAppContext();

  return (
    <Box
      flexDirection={{ base: 'column', tablet: 'row' }}
      flexWrap={{ base: 'nowrap', tablet: 'wrap' }}
      mb={{ base: 900, tablet: 950 }}
      whiteSpace="break-spaces"
      width="100%"
    >
      <AspectRatio
        ratio={{ base: 1, tablet: 4 / 3 }}
        width={{ base: '100%', desktop: '60%' }}
      >
        <Image
          alt="Take your place image"
          borderRadius={400}
          maxWidth="100%"
          src={image}
        />
      </AspectRatio>
      <Box
        alignSelf={{ desktop: 'center' }}
        flexDirection="column"
        marginTop={{ base: 500, desktop: 0 }}
        pl={{ desktop: 1000 }}
        width={{ base: '100%', tablet: '70%', desktop: '40%' }}
      >
        <Body
          as="span"
          color="neutral.550"
          fontWeight={700}
          mb={400}
          size={300}
        >
          {t('takeYourPlace.step', { number: index + 1 })}
        </Body>
        <Heading
          fontWeight={700}
          marginBottom="400 !important"
          size={{ base: 500, desktop: 700 }}
        >
          {title}
        </Heading>
        <Body
          as="span"
          color="neutral.500"
          fontWeight={400}
          marginBottom={600}
          size={300}
        >
          {description}
        </Body>
        <Button
          onClick={onAddPhotoClick}
          optionalTrackingProperties={{
            ...trackingData,
            primary_cta: false,
            feature: 'homeowner-web-app',
            page_or_screen_name: 'homeowner-app-home',
            track_id: `designer-lp-process-step-${index + 1}`,
            button_location: `Process step ${index + 1}`,
          }}
          size="large"
          variant="tertiary"
          width={{ base: '100%', tablet: 'fit-content' }}
        >
          {t('takeYourPlace.addPhoto')}
        </Button>
      </Box>
    </Box>
  );
};

const TakeYourPlace: React.FC<Props> = ({ onAddPhotoClick }) => {
  const t = useTranslate(TranslationNamespace.DESIGNER_LANDING_PAGE);

  const sections = [
    {
      title: (
        <Trans
          components={{
            LineBreak: <DesktopLineBreak />,
          }}
          i18nKey="takeYourPlace.designerYourDream.title"
          t={t}
        />
      ),
      description: t('takeYourPlace.designerYourDream.description'),
      image: dreamSpaceImage,
    },
    {
      title: (
        <Trans
          components={{ LineBreak: <DesktopLineBreak /> }}
          i18nKey="takeYourPlace.hoverYourHome.title"
          t={t}
        />
      ),
      description: t('takeYourPlace.hoverYourHome.description'),
      image: yourHomeImage,
    },
    {
      title: t('takeYourPlace.makeItReal.title'),
      description: t('takeYourPlace.makeItReal.description'),
      image: makeItReal,
    },
  ];

  return (
    <Box flexDir="column" mb={{ base: 700, tablet: 950 }} width="100%">
      <Heading
        marginBottom={{ base: 700, tablet: 900 }}
        marginTop={{ base: 1100, tablet: 1200 }}
      >
        {t('takeYourPlace.header')}
      </Heading>

      <Box flex={1} flexDirection="column">
        {sections.map(({ title, image, description }, index) => (
          <SectionCard
            description={description}
            image={image}
            index={index}
            key={description}
            onAddPhotoClick={onAddPhotoClick}
            title={title}
          />
        ))}
      </Box>
    </Box>
  );
};

export { TakeYourPlace };
